/**
 * Product option groups — metafield-driven option buttons (Style / Zip / Length, etc.) that
 * write their selections as cart line-item properties and gate the add-to-cart / checkout
 * buttons until every required group has a choice.
 *
 * This lives in a shared asset (rather than an inline <script> in the block) so the custom
 * element is also defined on collection/search pages. The quick-add modal fetches a product's
 * PDP markup and morphs it in; scripts injected that way do not execute, so without a globally
 * loaded definition the element would never upgrade inside the modal.
 */
class ProductOptionGroups extends HTMLElement {
  connectedCallback() {
    this._formId = this.dataset.formId;

    // Quick add re-renders this element's contents for a different product by morphing —
    // the <product-option-groups> element itself is reused, so connectedCallback runs only
    // once. Three consequences are handled below:
    //   1. Buttons get swapped out, so we delegate clicks from the element (surviving swaps)
    //      and derive selection state from the live DOM instead of caching it.
    //   2. The associated buy-buttons form is morphed into the modal AFTER this element, so
    //      it may not exist yet at connect time. We resolve it lazily every time (getter)
    //      rather than caching a possibly-null reference.
    //   3. The variant picker and Shopify re-enable / inject the add-to-cart and payment
    //      buttons on their own, so we watch the form and re-disable while options are
    //      incomplete.
    this.addEventListener('click', this._onBtnClick);

    // Re-evaluate whenever our own option markup is swapped for a different product.
    this._contentObserver = new MutationObserver(() => {
      this._bindForm();
      this._applyState();
    });
    this._contentObserver.observe(this, { childList: true, subtree: true });

    this._bindForm();
    this._applyState();

    // On the very first quick-add open this whole subtree — including the buy-buttons form —
    // is appended in one pass, so the form may not exist yet when the calls above run and the
    // content observer won't fire (children arrive fully-formed). Re-apply on the next frame,
    // by which point the morph has finished and the form is in the document.
    requestAnimationFrame(() => {
      if (!this.isConnected) return;
      this._bindForm();
      this._applyState();
    });
  }

  disconnectedCallback() {
    this._contentObserver?.disconnect();
    this._formObserver?.disconnect();
  }

  /** The buy-buttons form this option group feeds into. Resolved fresh each access. */
  get _form() {
    return this._formId ? document.getElementById(this._formId) : null;
  }

  /**
   * Attaches the submit guard and a live observer to the form once it exists. Safe to call
   * repeatedly — it only (re)binds when the form element actually changes (e.g. after a morph
   * swaps in a different product's form).
   */
  _bindForm() {
    const form = this._form;
    if (!form || form === this._boundForm) return;

    this._formObserver?.disconnect();
    form.addEventListener('submit', this._onFormSubmit, true);

    // Watch the form so that when the variant picker or Shopify's accelerated checkout script
    // (re-)enable or inject buttons, we immediately re-disable them if options are missing.
    this._formObserver = new MutationObserver(() => {
      if (!this._isComplete()) this._applyState();
    });
    this._formObserver.observe(form, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['disabled'],
    });

    this._boundForm = form;
  }

  _onBtnClick = (e) => {
    const btn = e.target.closest('.option-group__btn');
    if (!btn || !this.contains(btn)) return;

    const group = btn.closest('.option-group');
    const wasSelected = btn.classList.contains('is-selected');

    group.querySelectorAll('.option-group__btn').forEach((b) => {
      b.classList.remove('is-selected');
      b.setAttribute('aria-pressed', 'false');
    });

    const input = group.querySelector('.option-group__input');

    if (!wasSelected) {
      btn.classList.add('is-selected');
      btn.setAttribute('aria-pressed', 'true');
      if (input) input.value = btn.dataset.value || '';
    } else if (input) {
      input.value = '';
    }

    group.classList.remove('has-error');
    this._applyState();
  };

  _requiredGroups() {
    return Array.from(this.querySelectorAll('.option-group[data-required="true"]'));
  }

  _isComplete() {
    return this._requiredGroups().every((g) => g.querySelector('.option-group__btn.is-selected'));
  }

  _applyState() {
    this._bindForm();
    const disabled = !this._isComplete();
    const form = this._form;

    if (form) {
      form
        .querySelectorAll(
          'button[type="submit"], button[name="add"], .shopify-payment-button__button, .dynamic-checkout__button'
        )
        .forEach((btn) => {
          // Only write when it actually changes — writing the same value still mutates the
          // attribute, which the form observer would pick up and bounce back into here.
          if (btn.disabled !== disabled) btn.disabled = disabled;
        });
    }

    if (!disabled) this._clearErrors();
  }

  _onFormSubmit = (e) => {
    if (!this._isComplete()) {
      e.preventDefault();
      e.stopImmediatePropagation();
      this._showErrors();
    }
  };

  _showErrors() {
    this._requiredGroups().forEach((g) => {
      if (!g.querySelector('.option-group__btn.is-selected')) g.classList.add('has-error');
    });
    const msg = this.querySelector('.option-group__error-msg');
    if (msg) msg.removeAttribute('hidden');
    this.querySelector('.option-group.has-error')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  _clearErrors() {
    this.querySelectorAll('.option-group').forEach((g) => g.classList.remove('has-error'));
    this.querySelector('.option-group__error-msg')?.setAttribute('hidden', '');
  }
}

if (!customElements.get('product-option-groups')) {
  customElements.define('product-option-groups', ProductOptionGroups);
}
